import { FilterGroup } from '@customer/filters/types';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { CustomTimeRangeParams } from '../apis/helpers';
import { useAppSelector } from '../hooks';

export enum FaultLevel {
  One = '1',
  Two = '2',
  Three = '3',
}

export type FaultLevelKey = keyof typeof FaultLevel;
export const faultLevelKeys: FaultLevelKey[] = [...(Object.keys(FaultLevel) as FaultLevelKey[])];

export enum AssetDetailsAccordion {
  ReportingStatus = 'ReportingStatus',
  Components = 'Components',
  Location = 'Location',
}

export enum AssetConfigurationAccordion {
  SoftwareStatus = 'SoftwareStatus',
  GeofenceStatus = 'GeofenceStatus',
  NameStatus = 'NameStatus',
}

export enum AssetInteractionsAccordion {
  VehicleInteractions = 'VehicleInteractions',
  GeofenceInteractions = 'GeofenceInteractions',
}

export enum SidePanel {
  Asset,
  Help,
  Notifications,
  VehicleInteraction,
  GeofenceInteraction,
  TechnicalLibraryDoc,
  PartnerResource,
  Filters,
  AddInvestigationNote,
  AddAssetNote,
  InvestigationAsset,
  UserSettings,
  CreateCustomInvestigation,
  EditCustomInvestigation,
  EditLoggedInvestigation,
}

export enum AssetSidePanelTab {
  SelfTestSummary,
  Configuration,
  Details,
  Interactions,
}

export enum VehicleInteractionSidePanelTab {
  Summary,
}

export enum GeofenceInteractionSidePanelTab {
  Summary,
}

export enum NotificationsSidePanelTab {
  New,
  // Note: Future Techlibrary notification tabs will be enumerated here
}

interface SidePanelData {
  sidePanelOpened: boolean;
  sidePanel?: SidePanel;
  sidePanelTab?:
    | AssetSidePanelTab
    | NotificationsSidePanelTab
    | VehicleInteractionSidePanelTab
    | GeofenceInteractionSidePanelTab;
  sidePanelDefaultAccordion?: string;
  sidePanelModelId?: string;
  sidePanelFilters?: FilterGroup;
}

export enum SelectionTimeRange {
  Hours24 = '0',
  Days7 = '1',
  Days30 = '2',
  Months2 = '3',
  Months3 = '4',
  // Front-end only, never send to the API.
  Custom = 'custom',
}

export type GlobalTimeRange = Exclude<
  SelectionTimeRange,
  SelectionTimeRange.Months2 | SelectionTimeRange.Months3 | SelectionTimeRange.Custom
>;

export const globalTimeRangeValues: GlobalTimeRange[] = [
  SelectionTimeRange.Hours24,
  SelectionTimeRange.Days7,
  SelectionTimeRange.Days30,
];

export const isGlobalTimeRange = (value: SelectionTimeRange): value is GlobalTimeRange => {
  return globalTimeRangeValues.includes(value as GlobalTimeRange);
};

export type TimeRange = Exclude<SelectionTimeRange, SelectionTimeRange.Custom>;

export interface GlobalData extends SidePanelData {
  timeRange: GlobalTimeRange;
  performanceTimeRange: SelectionTimeRange;
  performanceTimeRangeCustom: Partial<CustomTimeRangeParams>;
  isWelcomeModalSeen: boolean;
}

export interface ActionOpenBaseModel {
  sidePanel: SidePanel;
  tab?:
    | AssetSidePanelTab
    | NotificationsSidePanelTab
    | VehicleInteractionSidePanelTab
    | GeofenceInteractionSidePanelTab;
  modelId?: string;
  defaultAccordion?: string;
  filters?: FilterGroup;
}

export interface ActionOpenAsset extends ActionOpenBaseModel {
  sidePanel: SidePanel.Asset;
  tab?: AssetSidePanelTab;
  modelId: string;
}

export interface ActionOpenVehicleInteraction extends ActionOpenBaseModel {
  sidePanel: SidePanel.VehicleInteraction;
  tab?: VehicleInteractionSidePanelTab;
  modelId: string;
}

export interface ActionOpenGeofenceInteraction extends ActionOpenBaseModel {
  sidePanel: SidePanel.GeofenceInteraction;
  tab?: GeofenceInteractionSidePanelTab;
  modelId: string;
}

interface ActionOpenAssetSelfTest extends ActionOpenAsset {
  tab: AssetSidePanelTab.SelfTestSummary;
  defaultAccordion?: FaultLevel;
}

interface ActionOpenAssetConfiguration extends ActionOpenAsset {
  tab: AssetSidePanelTab.Configuration;
  defaultAccordion?: AssetConfigurationAccordion;
}

interface ActionOpenAssetDetails extends ActionOpenAsset {
  tab: AssetSidePanelTab.Details;
  defaultAccordion?: AssetDetailsAccordion;
}

interface ActionOpenAssetEvents extends ActionOpenAsset {
  tab: AssetSidePanelTab.Interactions;
}

interface ActionOpenNotifications extends ActionOpenBaseModel {
  sidePanel: SidePanel.Notifications;
  tab?: NotificationsSidePanelTab;
}

interface ActionOpenFilters extends ActionOpenBaseModel {
  sidePanel: SidePanel.Filters;
  filters: FilterGroup;
}

export type ActionOpenSidePanel =
  | ActionOpenAssetSelfTest
  | ActionOpenAssetConfiguration
  | ActionOpenAssetDetails
  | ActionOpenAssetEvents
  | ActionOpenNotifications
  | ActionOpenVehicleInteraction
  | ActionOpenGeofenceInteraction
  | ActionOpenFilters
  | ActionOpenBaseModel;

const initialSidePanelData: SidePanelData = {
  sidePanelOpened: false,
  sidePanel: undefined,
  sidePanelTab: undefined,
  sidePanelDefaultAccordion: undefined,
  sidePanelModelId: undefined,
  sidePanelFilters: undefined,
};

const initialCustomTimeRangeData: Partial<CustomTimeRangeParams> = {
  timeRangeStart: undefined,
  timeRangeEnd: undefined,
};

const initialData: GlobalData = {
  timeRange: SelectionTimeRange.Hours24,
  performanceTimeRange: SelectionTimeRange.Hours24,
  performanceTimeRangeCustom: { ...initialCustomTimeRangeData },
  isWelcomeModalSeen: false,
  ...initialSidePanelData,
};

const globalSlice = createSlice({
  name: 'global',
  initialState: initialData,
  reducers: {
    setGlobalTimeFilter: (state, action: PayloadAction<GlobalTimeRange>) => ({
      ...state,
      // Keep the multiple time range filters in sync when a common value is selected.
      timeRange: action.payload,
      performanceTimeRange: action.payload,
    }),
    setPerformanceTimeFilter: (state, action: PayloadAction<SelectionTimeRange>) => ({
      ...state,
      // Only update the global time range filter if a common value is selected.
      timeRange: isGlobalTimeRange(action.payload) ? action.payload : state.timeRange,
      performanceTimeRange: action.payload,
    }),
    setPerformanceTimeRangeCustom: (state, action: PayloadAction<CustomTimeRangeParams>) => ({
      ...state,
      performanceTimeRange: SelectionTimeRange.Custom,
      performanceTimeRangeCustom: action.payload,
    }),
    openSidePanel: (state, action: PayloadAction<ActionOpenSidePanel>) => ({
      ...state,
      sidePanel: action.payload.sidePanel,
      sidePanelModelId: action.payload.modelId,
      sidePanelTab: action.payload.tab,
      sidePanelDefaultAccordion: action.payload.defaultAccordion,
      sidePanelFilters: action.payload.filters,
      sidePanelOpened: true,
    }),
    closeSidePanel: state => ({
      ...state,
      ...initialSidePanelData,
    }),
    setWelcomeModalSeen: state => ({
      ...state,
      isWelcomeModalSeen: true,
    }),
  },
});

export const {
  setGlobalTimeFilter,
  setPerformanceTimeFilter,
  setPerformanceTimeRangeCustom,
  setWelcomeModalSeen,
  openSidePanel,
  closeSidePanel,
} = globalSlice.actions;

export default globalSlice.reducer;

export interface PerformanceTimeRangeFilters {
  timeRangeStart?: string;
  timeRangeEnd?: string;
  timeRange?: TimeRange;
}

export const usePerformanceTimeRangeFilters = (): PerformanceTimeRangeFilters => {
  const {
    performanceTimeRange,
    performanceTimeRangeCustom: { timeRangeStart, timeRangeEnd },
  } = useAppSelector(state => state.global);

  if (performanceTimeRange === SelectionTimeRange.Custom) {
    if (timeRangeStart && timeRangeEnd) {
      return {
        timeRangeStart,
        timeRangeEnd,
      };
    } else {
      // This code should not be reached, but default to 24 hours just in case.
      return { timeRange: SelectionTimeRange.Hours24 };
    }
  } else {
    return { timeRange: performanceTimeRange };
  }
};
